import { Bath } from "lucide-react";
import ServicePage from "./ServicePage";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "../components/ui/carousel";
import { useServicePhotos } from "../hooks/useServicePhotos";

const basePhotos = [
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/ss6esrjy_IMG_0010.jpeg", alt: "Moderne toilet renovatie - Badkamer renovatie Breukelen" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/dnlzm1vx_IMG_0001.jpeg", alt: "Oude badkamer voor renovatie - Cardoso Bouw" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/6t3nje3k_IMG_0009.jpeg", alt: "Afgewerkte badkamer met bad en meubel - Cardoso Bouw en Onderhoud" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/uyja0qe2_IMG_0002.jpeg", alt: "Badkamer tijdens renovatie - Cardoso Bouw en Onderhoud" },
];

const faqs = [
  {
    q: "Hoe lang duurt een complete badkamer renovatie?",
    a: "Gemiddeld 2 tot 3 weken, afhankelijk van de grootte van de badkamer en de gekozen materialen. Vooraf ontvangt u een duidelijke planning zodat u weet waar u aan toe bent.",
  },
  {
    q: "Wat kost een badkamer renovatie?",
    a: "De kosten zijn afhankelijk van de afmetingen, het sanitair, het tegelwerk en uw wensen. Wij stellen altijd een transparante offerte op maat op, zonder verrassingen achteraf. De offerte is gratis en vrijblijvend.",
  },
  {
    q: "Verzorgen jullie ook het leidingwerk en de elektra?",
    a: "Ja. Wij verzorgen het sloopwerk, leidingwerk, de waterdichte afwerking, het tegelwerk, het sanitair, vloerverwarming en ventilatie. Voor elektra werken wij samen met gecertificeerde partners, zodat alles veilig en volgens de normen wordt aangesloten.",
  },
  {
    q: "Kan ik zelf mijn sanitair en tegels uitkiezen?",
    a: "Zeker. U kiest zelf uw sanitair en tegels; wij adviseren graag over kwaliteit, een praktische indeling en onderhoud. Wij kunnen ook meegaan naar de showroom of materialen voor u inkopen.",
  },
  {
    q: "Kan ik tijdens de renovatie thuis blijven wonen?",
    a: "Ja, in de meeste gevallen wel. Wij werken netjes, dekken alles goed af en beperken de overlast tot een minimum. Houd er rekening mee dat de badkamer tijdens de werkzaamheden niet te gebruiken is.",
  },
  {
    q: "Renoveren jullie ook kleine badkamers en toiletten?",
    a: "Ja, van een compacte toiletruimte tot een grote luxe badkamer met inloopdouche en vrijstaand bad. Juist in kleine ruimtes maakt een slimme indeling het verschil; daar denken wij graag over mee.",
  },
];

function BadkamerCarousel() {
  const photos = useServicePhotos("badkamer", basePhotos);
  
  return (
    <div className="px-12" data-testid="badkamer-carousel">
      <Carousel opts={{ loop: true }} className="w-full">
        <CarouselContent>
          {photos.map((photo, i) => (
            <CarouselItem key={i}>
              <div className="overflow-hidden">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="bg-[#005F5F] text-white border-none hover:bg-[#C5A059] hover:text-white" />
        <CarouselNext className="bg-[#005F5F] text-white border-none hover:bg-[#C5A059] hover:text-white" />
      </Carousel>
    </div>
  );
}

export default function BadkamerPage() {
  return (
    <ServicePage
      seoTitle="Badkamer Renovatie Breukelen | Vakmannen - Cardoso Bouw"
      seoDescription="Complete badkamer renovatie in Breukelen door ervaren vakmannen. Van sloopwerk tot oplevering. Tegelwerk, sanitair, leidingen. Vraag een vrijblijvende offerte aan."
      seoKeywords="badkamer renovatie Breukelen, badkamer verbouwen Breukelen, badkamer renoveren Utrecht, badkamer renoveren Hilversum, nieuwe badkamer, badkamer specialist, badkamer renovatie Loosdrecht, badkamer renovatie Amsterdam"
      title="Badkamer Renovatie"
      subtitle="Badkamer renovatie Breukelen"
      heroImage="https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/6t3nje3k_IMG_0009.jpeg"
      icon={Bath}
      description="Droomt u van een nieuwe badkamer? Cardoso Bouw en Onderhoud verzorgt complete badkamer renovaties in Breukelen en omgeving. Van de eerste sloophamer tot de laatste voeg, ons team van ervaren vakmannen zorgt voor een perfect eindresultaat. Wij begeleiden u door het hele proces: van ontwerp en materiaaladvies tot de volledige uitvoering en oplevering."
      features={[
        "Sloopwerk bestaande badkamer",
        "Leidingwerk en installatie",
        "Waterdichte afwerking",
        "Tegelwerk (wand en vloer)",
        "Montage sanitair en kranen",
        "Plaatsen van douche, bad en toilet",
        "Verwarming en vloerverwarming",
        "Ventilatie en elektrisch werk",
      ]}
      faqs={faqs}
      extraContent={
        <div className="space-y-8">
          {/* PROJECT CAROUSEL */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A] mb-6">
              Onze badkamer projecten
            </h2>
            <BadkamerCarousel />
          </div>

          <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A]">
            Complete badkamer renovatie op maat
          </h2>
          <p className="text-slate-600 text-base leading-relaxed">
            Een badkamer renovatie is maatwerk. Elke badkamer is anders en elke klant heeft 
            eigen wensen en voorkeuren. Bij Cardoso Bouw en Onderhoud bespreken wij samen uw 
            ideeën en vertalen die naar een praktisch ontwerp dat past bij uw stijl en budget. 
            Van een luxe inloopdouche met regendouche tot een praktisch familiebad met ruime 
            opbergruimte, wij realiseren het voor u.
          </p>
          <p className="text-slate-600 text-base leading-relaxed">
            Alle werkzaamheden worden door ons eigen team uitgevoerd. Dit betekent dat u een 
            vast aanspreekpunt heeft gedurende het hele traject. Geen gedoe met verschillende 
            aannemers en onderaannemers, wij regelen alles voor u.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Het renovatieproces stap voor stap
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Onze badkamer renovatie begint met een vrijblijvend adviesgesprek bij u thuis. 
            Wij bekijken de huidige situatie, bespreken uw wensen en geven advies over 
            materialen en indeling. Daarna ontvangt u een gedetailleerde offerte met een 
            duidelijke tijdsplanning.
          </p>
          <p className="text-slate-600 text-base leading-relaxed">
            De uitvoering start met het zorgvuldig slopen van de bestaande badkamer. 
            Vervolgens leggen wij nieuwe leidingen, brengen waterdichte lagen aan, 
            tegelen wanden en vloeren en monteren het sanitair. Gemiddeld is een 
            badkamer renovatie binnen 2 tot 3 weken afgerond.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Badkamer renovatie in de regio
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Wij voeren badkamer renovaties uit in heel Noord-Holland, Zuid-Holland, Utrecht en Flevoland. 
            Wij komen graag bij u langs voor een vrijblijvende offerte. Met meer dan 8 jaar ervaring 
            en tientallen badkamer projecten op ons naam, weet u dat u in goede handen bent. 
            Onze klanten waarderen ons met 5 sterren op Google Reviews.
          </p>
        </div>
      }
    />
  );
}
